import React from 'react';

interface Experience {
  company: string;
  title: string;
  location: string;
  date: string;
  points: string[];
}

interface Education {
  school: string;
  degree: string;
  date: string;
}

function ResumePage() {
  const experiences: Experience[] = [
    {
      company: 'Amazon Web Services (AWS)',
      title: 'Software Development Engineer II',
      location: 'Santa Clara, CA',
      date: 'Apr 2024 — Present',
      points: [
        'Tier-1 Service Ownership: Primary owner for two tier-1 AWS Networking services operating at 50k sustained QPS across 35+ regions with 99.9% SLA; services support 200+ internal AWS services and are critical path for global datacenter expansion including AI compute capacity.',
        'Search Platform: Built full-text search system for 2B+ network configuration documents, enabling previously-impossible queries. Owned end-to-end: architecture, ingestion pipeline, indexing, and production rollout (OpenSearch, Python, Java, Kotlin). Delivered in 4 months.',
        'Network Visibility Platform: Led full-stack design and implementation of centralized observability platform providing real-time visibility into network configuration changes across all AWS datacenters. Built backend (Java/Kotlin) and frontend (React/TypeScript).',
        'Operational Leadership: Participated in on-call rotation for tier-1 services; led incident response, post-incident reviews, and org-level operational review process.',
      ],
    },
    {
      company: 'Amazon Web Services (AWS)',
      title: 'Software Development Engineer',
      location: 'Santa Clara, CA',
      date: 'Oct 2022 — Apr 2024',
      points: [
        'Led team of 2 engineers to architect and execute modernization and rollout of core network configuration service; migrated to modern architecture using Java/Kotlin and AWS infrastructure, improving maintainability and scalability.',
        'Implemented and led the production rollout of a redesigned OS image distribution service operating across all AWS regions, replacing a legacy system and reducing end-to-end image propagation SLA from 30 days to under 5 minutes.',
        'Optimized OS image distribution service performance; improved p99 latency by 70% and reduced infrastructure cost by 60% while handling increased traffic volume.',
        'Mentored junior engineers and interns; led intern projects delivering production features adopted by AWS Networking teams, successfully sponsored an intern for full-time conversion.',
      ],
    },
    {
      company: 'Amazon Web Services (AWS)',
      title: 'Cyber Security Engineer',
      location: 'Santa Clara, CA',
      date: 'Apr 2022 — Oct 2022',
      points: [
        'Developed a microservice-based security vulnerability scanning platform that automated manual scanning, eliminating the need for hands-on execution by security engineers. Built in Java with infrastructure defined in AWS CDK.',
      ],
    },
    {
      company: 'Kasada',
      title: 'Cyber Security Engineer',
      location: 'Walnut Creek, CA',
      date: 'Oct 2021 — Apr 2022',
      points: [
        'Implemented bot detection and behavioral profiling systems to mitigate large-scale automated abuse, using Python, TypeScript, and AWS CDK.',
      ],
    },
    {
      company: 'Hewlett Packard Enterprise',
      title: 'Cyber Security Engineer',
      location: 'Roseville, CA',
      date: 'Jun 2020 — Sep 2021',
      points: [
        'Deployed a SOAR platform and built Python automation to streamline incident response, integrating malware, network traffic, and endpoint analysis.',
      ],
    },
    {
      company: 'Hewlett Packard Enterprise',
      title: 'Cyber Security Intern',
      location: 'Roseville, CA',
      date: 'Jun 2019 — Aug 2019',
      points: [
        'Built a full-stack incident response ticketing system using Python, PHP, JavaScript, and MySQL.',
      ],
    },
    {
      company: 'University of California, Davis',
      title: 'Cybersecurity Teaching Assistant',
      location: 'Sacramento, CA',
      date: 'Jan 2020 — Jul 2021',
      points: [
        'Taught and supported hands-on software security coursework covering network security, malware analysis, and digital forensics.',
      ],
    },
  ];

  const education: Education[] = [
    {
      school: 'California State University, Sacramento',
      degree: 'Bachelor of Science in Computer Science',
      date: 'Jan 2018 — May 2020',
    },
  ];

  const skills =
    'Java, Python, Kotlin, TypeScript; AWS (EC2, S3, DynamoDB, OpenSearch, IAM, CDK); Distributed systems, search infrastructure, observability, high-availability services';

  return (
    <div className="resume-sheet max-w-4xl mx-auto px-2 sm:px-4 pt-2 pb-4 sm:pt-3 sm:pb-8">
      <div className="glass-card rounded-3xl p-3 sm:p-8 aero-glow ring-1 ring-white/20">
        <header className="text-center mb-8 sm:mb-12">
          <h1 className="text-4xl sm:text-5xl font-display font-semibold resume-heading mb-4 tracking-tight">
            Daniel Kuzmin
          </h1>
          <div className="flex flex-col sm:flex-row justify-center items-center gap-2 sm:gap-0 sm:space-x-4 resume-text-muted font-light text-sm sm:text-base">
            <span>Santa Clara, CA</span>
            <span className="hidden sm:inline opacity-70">•</span>
            <a href="mailto:danielkuzmin@gmail.com" className="resume-link break-all">
              danielkuzmin@gmail.com
            </a>
            <span className="hidden sm:inline opacity-70">•</span>
            <a href="tel:+19166000441" className="resume-link whitespace-nowrap">
              +1 (916) 600-0441
            </a>
            <span className="hidden sm:inline opacity-70">•</span>
            <a
              href="https://linkedin.com/in/danielkuzmin"
              className="resume-link break-all"
              target="_blank"
              rel="noopener noreferrer"
            >
              linkedin.com/in/danielkuzmin
            </a>
          </div>
        </header>

        <section className="mb-8 sm:mb-12">
          <h2 className="text-xl sm:text-2xl font-display font-medium resume-heading mb-4 sm:mb-6 border-b border-white/25 pb-2">
            Experience
          </h2>
          <div className="space-y-6 sm:space-y-8">
            {experiences.map((exp, index) => (
              <div
                key={index}
                className="resume-panel p-3 sm:p-6"
              >
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-2">
                  <div>
                    <h3 className="text-lg sm:text-xl resume-heading">{exp.company}</h3>
                    <p className="resume-text text-[15px] sm:text-base">{exp.title}</p>
                    <p className="resume-text-muted text-sm">{exp.location}</p>
                  </div>
                  <span className="resume-text-muted text-sm mt-2 sm:mt-0 shrink-0">{exp.date}</span>
                </div>
                <ul className="list-disc list-inside resume-text space-y-2.5 mt-4">
                  {exp.points.map((point, i) => (
                    <li key={i} className="text-sm sm:text-[15px] leading-relaxed pl-4 -indent-4 break-words">
                      {point}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-8 sm:mb-12">
          <h2 className="text-xl sm:text-2xl font-display font-medium resume-heading mb-4 sm:mb-6 border-b border-white/25 pb-2">
            Education
          </h2>
          <div className="space-y-6 sm:space-y-8">
            {education.map((edu, index) => (
              <div
                key={index}
                className="resume-panel p-4 sm:p-6"
              >
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-2">
                  <div>
                    <h3 className="text-lg sm:text-xl resume-heading">{edu.school}</h3>
                    <p className="resume-text">{edu.degree}</p>
                  </div>
                  <span className="resume-text-muted text-sm mt-2 sm:mt-0 shrink-0">{edu.date}</span>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section>
          <h2 className="text-xl sm:text-2xl font-display font-medium resume-heading mb-4 sm:mb-6 border-b border-white/25 pb-2">
            Skills
          </h2>
          <div className="resume-panel p-4 sm:p-6">
            <p className="resume-text text-sm sm:text-[15px] leading-relaxed">{skills}</p>
          </div>
        </section>
      </div>
    </div>
  );
}

export default ResumePage;
